/**
 * useNotificationsStream — opens the SSE notifications stream while a
 * user is signed in and:
 *
 *   - invalidates the cached notifications + unread-count queries so
 *     pages relying on React Query refetch on the next interaction
 *   - patches the unread-count cache directly so the bell badge bumps
 *     instantly without a round-trip
 *   - pops a toast for each incoming notification
 *
 * Mount once at the layout level — the stream is a singleton and
 * mounting it deeper would open multiple connections.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { connectNotificationsStream } from "@/lib/notifications-stream";
import { ROLE_BASE, type Role } from "@/components/layouts/nav-config";
import type { Notification } from "@/types/api";

export function useNotificationsStream(): void {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const base = ROLE_BASE[(user?.role ?? "vessel") as Role];

  useEffect(() => {
    if (!user) return;

    const close = connectNotificationsStream({
      onNotification: (n) => {
        // Refetch the lists on next access. invalidate (not setQueryData)
        // is fine here because the user is most likely on a page that
        // already shows the bell, not the full list — keeping the
        // full-list data lazy.
        qc.invalidateQueries({ queryKey: ["notifications"] });

        // Bump the unread-count cache eagerly so the badge updates the
        // instant the SSE frame lands. The key + shape must match what
        // useNotifications actually reads: ["notifications","unread-count"]
        // holding a PLAIN NUMBER (readThrough unwraps the API envelope) —
        // this previously wrote an ApiOk object into ["notifications-unread"],
        // a key nobody reads, so the instant bump was a silent no-op.
        const prev = qc.getQueryData<number>(["notifications", "unread-count"]);
        if (typeof prev === "number") {
          qc.setQueryData<number>(["notifications", "unread-count"], prev + 1);
        } else {
          qc.invalidateQueries({ queryKey: ["notifications", "unread-count"] });
        }

        toast.info(n.title, {
          description: n.message ?? undefined, position: "top-center",
        });
      },
      onConnect: () => {
        // First connect (and every reconnect) — pull a fresh count so we
        // don't drift if events were missed while disconnected.
        qc.invalidateQueries({ queryKey: ["notifications", "unread-count"] });
      },
    });

    // Mobile resume resync: the OS freezes JS (and drops the SSE socket)
    // while the app is backgrounded, and the stream's reconnect backoff can
    // take up to 30s after resume. Refetch counts + lists the moment the
    // webview becomes visible again so the bell is right away, not
    // eventually.
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void qc.invalidateQueries({ queryKey: ["notifications"] });
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      close();
    };
    // user.id is the right dep — re-opening on every user state change
    // (e.g. profile edits) would needlessly cycle the connection.
  }, [user?.id, qc, navigate, base]);
}

// Hint to TS that the unused Notification type is intentionally re-exported.
export type { Notification };
